import React from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { Routes, Route, useNavigate } from 'react-router-dom';

// 從組件文件夾導入
import CCTVViewer from '../components/CCTVViewer';
import UtilityMeters from '../components/UtilityMeters';
import Notifications from '../components/Notifications';
import PaymentUpload from '../components/PaymentUpload';
import VisitorAccess from '../components/VisitorAccess';

const DashboardHome = () => {
  const { user } = useSelector(state => state.auth);
  const { notifications } = useSelector(state => state.notifications);
  const unreadNotifications = notifications ? notifications.filter(n => !n.read).slice(0, 3) : [];
  
  const handleNavigate = (path) => {
    // 導航邏輯
    console.log(`導航到: ${path}`);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {user?.tenantName ? `${user.tenantName} - 租戶儀表板` : '租戶儀表板'}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" gutterBottom>
        歡迎回來，{user?.name || '用戶'}。以下是您的租賃情況概覽。
      </Typography>
      
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* 租約資訊卡 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                租約資訊
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">
                  <strong>空間:</strong> {user?.unit || '商業單位 A-101'}
                </Typography>
                <Typography variant="body1">
                  <strong>租期:</strong> {user?.leaseStart || '2023-01-01'} 至 {user?.leaseEnd || '2025-12-31'}
                </Typography>
                <Typography variant="body1">
                  <strong>月租金:</strong> {user?.rent || 'HK$25,000'}
                </Typography>
                <Typography variant="body1">
                  <strong>下次付款日:</strong> {user?.nextPayment || '2023-06-01'}
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                startIcon={<AccountBalanceIcon />}
                onClick={() => handleNavigate('/tenant/lease')}
              >
                查看完整租約
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 繳費狀態卡 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                繳費狀態
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="success.main">
                  <strong>本月:</strong> 已繳費
                </Typography>
                <Typography variant="body1">
                  <strong>下期繳費日:</strong> 2023-06-01
                </Typography>
                <Typography variant="body1">
                  <strong>繳費方式:</strong> 銀行轉賬
                </Typography>
                <Typography variant="body1">
                  <strong>延緩繳費次數:</strong> 0
                </Typography>
              </Box>
              <Button 
                variant="contained" 
                startIcon={<PaymentIcon />}
                onClick={() => handleNavigate('/tenant/payments')}
              >
                繳交租金
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 快速操作區 */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            快速操作
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/tenant/upload-receipt')}>
                <CardContent>
                  <ReceiptIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">上傳收據</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/tenant/notifications')}>
                <CardContent>
                  <NotificationsIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">查看通知</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/tenant/maintenance')}>
                <CardContent>
                  <NoteAddIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">提交維修</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleNavigate('/tenant/history')}>
                <CardContent>
                  <AccessTimeIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="body1">歷史記錄</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        
        {/* 最近通知 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                最近通知
              </Typography>
              {unreadNotifications.length > 0 ? (
                <List>
                  {unreadNotifications.map((notification) => (
                    <ListItem key={notification.id} divider>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <NotificationsIcon />
                      </Avatar>
                      <ListItemText 
                        primary={notification.title} 
                        secondary={notification.date}
                      />
                    </ListItem>
                  ))}
                  {unreadNotifications.length > 0 && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Button 
                        onClick={() => handleNavigate('/tenant/notifications')}
                        size="small"
                      >
                        查看全部
                      </Button>
                    </Box>
                  )}
                </List>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  沒有新通知
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const TenantDashboard = () => {
  return (
    <Routes>
      <Route index element={<DashboardHome />} />
      <Route path="cctv" element={<CCTVViewer />} />
      <Route path="meters" element={<UtilityMeters />} />
      <Route path="notifications" element={<Notifications />} />
      <Route path="payments" element={<PaymentUpload />} />
      <Route path="visitor-access" element={<VisitorAccess />} />
      <Route path="announcements" element={<Box sx={{ p: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          物業通告
        </Typography>
        <Notifications />
      </Box>} />
    </Routes>
  );
};

export default TenantDashboard; 